import { forwardRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { agentRoleLabel } from "@/lib/agent-display";
import { AgentChatAvatar } from "@/components/chat/agent-chat-avatar";

// ── @-mention candidates ────────────────────────────────────────────
//
// The chat input's @ trigger surfaces two kinds of candidates in one
// dropdown: peer agents (message routing — existing behaviour) and
// files from the current agent's VM workspace (inserted as a path the
// agent can read). Both chat surfaces (workspace ChatSurface and the
// agent detail chat tab) share this module so the picker behaves the
// same everywhere.

export type MentionCandidate =
  | { kind: "agent"; id: string; name: string; role: string; avatarUrl: string | null }
  | { kind: "file"; path: string; name: string };

/** Text inserted into the input when a file candidate is picked. Files are
 *  plain workspace-relative paths, not @mentions, so they cannot be mistaken
 *  for routed agent mentions by the send-time regex. */
export function fileMentionText(path: string): string {
  return `${path} `;
}

interface WorkspaceFileEntry {
  name: string;
  path: string;
  isDir: boolean;
}

/** Files in the agent's workspace (recursive, server-capped). Only
 *  fetched while the mention dropdown is open; cached briefly so
 *  keystrokes don't refetch. */
export function useWorkspaceFileMentions(
  agentId: string | null | undefined,
  enabled: boolean,
): { name: string; path: string }[] {
  const { data } = useQuery({
    queryKey: ["workspace-files-recursive", agentId],
    queryFn: async () => {
      const res = await fetch(`/api/agents/${agentId}/workspace?recursive=true`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to list workspace (${res.status})`);
      const listing = (await res.json()) as { entries: WorkspaceFileEntry[] };
      return listing.entries
        .filter((e) => !e.isDir)
        .map((e) => ({ name: e.name, path: e.path }));
    },
    enabled: enabled && !!agentId,
    staleTime: 30_000,
    retry: false,
  });
  return data ?? [];
}

const MAX_AGENT_CANDIDATES = 6;
const MAX_FILE_CANDIDATES = 8;

/** Merge agents + workspace files into one keyboard-navigable list,
 *  filtered by the text typed after "@". Agents first (routing is the
 *  more common intent), then files matched on name or path. */
export function buildMentionCandidates(
  agents: Array<{ id: string; name: string; role: string; avatarUrl: string | null }>,
  files: Array<{ name: string; path: string }>,
  query: string,
): MentionCandidate[] {
  const q = query.trim().toLowerCase();
  const agentMatches: MentionCandidate[] = agents
    .filter((a) => !q || a.name.toLowerCase().includes(q))
    .slice(0, MAX_AGENT_CANDIDATES)
    .map((a) => ({ kind: "agent", id: a.id, name: a.name, role: a.role, avatarUrl: a.avatarUrl }));
  const fileMatches: MentionCandidate[] = files
    .filter((f) => !q || f.name.toLowerCase().includes(q) || f.path.toLowerCase().includes(q))
    .slice(0, MAX_FILE_CANDIDATES)
    .map((f) => ({ kind: "file", path: f.path, name: f.name }));
  return [...agentMatches, ...fileMatches];
}

type MentionHighlightKind = "text" | "agent" | "file";

interface MentionHighlightSegment {
  kind: MentionHighlightKind;
  text: string;
}

function isMentionBoundary(value: string, start: number, end: number): boolean {
  const before = start === 0 ? "" : value[start - 1];
  const after = end >= value.length ? "" : value[end];
  return (!before || /\s/.test(before)) && (!after || /\s|[.,!?;:)]/.test(after));
}

function findLiteralMatches(
  value: string,
  needle: string,
  kind: Exclude<MentionHighlightKind, "text">,
  boundary: boolean,
): Array<{ start: number; end: number; kind: Exclude<MentionHighlightKind, "text"> }> {
  const matches: Array<{ start: number; end: number; kind: Exclude<MentionHighlightKind, "text"> }> = [];
  const lowerValue = value.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  let index = lowerValue.indexOf(lowerNeedle);
  while (index !== -1) {
    const end = index + needle.length;
    if (!boundary || isMentionBoundary(value, index, end)) {
      matches.push({ start: index, end, kind });
    }
    index = lowerValue.indexOf(lowerNeedle, end);
  }
  return matches;
}

function buildMentionHighlightSegments(
  value: string,
  agents: Array<{ name: string }>,
  files: Array<{ path: string }>,
): MentionHighlightSegment[] {
  if (!value) return [{ kind: "text", text: "\u200b" }];

  const matches = [
    ...agents.flatMap((agent) => (
      agent.name ? findLiteralMatches(value, `@${agent.name}`, "agent", true) : []
    )),
    ...files.flatMap((file) => (
      file.path ? findLiteralMatches(value, file.path, "file", true) : []
    )),
  ].sort((a, b) => (a.start - b.start) || ((b.end - b.start) - (a.end - a.start)));

  const segments: MentionHighlightSegment[] = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start < cursor) continue;
    if (match.start > cursor) {
      segments.push({ kind: "text", text: value.slice(cursor, match.start) });
    }
    segments.push({ kind: match.kind, text: value.slice(match.start, match.end) });
    cursor = match.end;
  }
  if (cursor < value.length) {
    segments.push({ kind: "text", text: value.slice(cursor) });
  }
  if (value.endsWith("\n")) {
    segments.push({ kind: "text", text: "\u200b" });
  }
  return segments;
}

export const MentionInputHighlight = forwardRef<
  HTMLDivElement,
  {
    value: string;
    agents: Array<{ name: string }>;
    files: Array<{ path: string }>;
    className?: string;
  }
>(function MentionInputHighlight({ value, agents, files, className }, ref) {
  const segments = buildMentionHighlightSegments(value, agents, files);
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={cn(
        "pointer-events-none whitespace-pre-wrap break-words text-text-primary",
        className,
      )}
    >
      {segments.map((segment, index) => (
        <span
          // Text can repeat, so index is the stable key for this mirror layer.
          key={`${segment.kind}:${index}`}
          className={cn(
            segment.kind === "agent" && "font-medium text-text-secondary",
            segment.kind === "file" && "rounded bg-surface-hover px-1 py-0.5 font-mono text-[0.92em] text-text-primary",
          )}
        >
          {segment.text}
        </span>
      ))}
    </div>
  );
});

export function MentionDropdown({
  candidates,
  selectedIndex,
  anchorEl,
  onSelect,
}: {
  candidates: MentionCandidate[];
  selectedIndex: number;
  anchorEl: HTMLElement;
  onSelect: (candidate: MentionCandidate) => void;
}) {
  const rect = anchorEl.getBoundingClientRect();
  const firstAgentIdx = candidates.findIndex((c) => c.kind === "agent");
  const firstFileIdx = candidates.findIndex((c) => c.kind === "file");
  return (
    <div
      className="fixed w-80 max-h-64 overflow-y-auto rounded-xl border border-border bg-background shadow-xl"
      style={{
        left: rect.left,
        top: rect.top - 4,
        transform: "translateY(-100%)",
        zIndex: 9999,
      }}
    >
      {candidates.map((c, i) => (
        <div key={c.kind === "agent" ? `a:${c.id}` : `f:${c.path}`}>
          {i === firstAgentIdx && (
            <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
              Agents
            </p>
          )}
          {i === firstFileIdx && (
            <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
              Workspace files
            </p>
          )}
          <button
            onMouseDown={(e) => { e.preventDefault(); onSelect(c); }}
            className={cn(
              "flex w-full items-center gap-2 px-3 py-2 text-sm",
              i === selectedIndex
                ? "bg-accent/10 text-accent"
                : "text-text-secondary hover:bg-surface-hover",
            )}
          >
            {c.kind === "agent" ? (
              <>
                <AgentChatAvatar avatarUrl={c.avatarUrl} name={c.name} />
                <span className="truncate font-medium text-text-secondary">{c.name}</span>
                <span className="ml-auto text-xs text-text-muted">{agentRoleLabel(c)}</span>
              </>
            ) : (
              <>
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-surface-hover text-text-muted">
                  <FileText size={13} />
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="inline-block max-w-full truncate rounded bg-surface-hover px-1.5 py-0.5 font-mono text-[12px] font-medium text-text-primary">
                    {c.name}
                  </span>
                  {c.path !== c.name && (
                    <span className="block truncate font-mono text-[11px] text-text-muted">{c.path}</span>
                  )}
                </span>
              </>
            )}
          </button>
        </div>
      ))}
    </div>
  );
}
